import type { FacetField, SelectableField } from "../../types";
import type { AssetRecord } from "../graph/buildAssetDocsGraph";
import type { AssetProfile } from "./assetProfiles";

export interface LevelOption {
    field: string;
    label: string;
    value: string;
}

export interface StageSearchHit {
    machine?: AssetRecord;
    level?: LevelOption;
}

export const isAssetSearchField = ({
    profile,
    field,
}: {
    profile: AssetProfile;
    field: string;
}): boolean => field === profile.nameField || field === profile.keyField;

export const buildLevelOptions = ({
    facets,
    fields,
}: {
    facets: FacetField[] | undefined;
    fields: SelectableField[];
}): LevelOption[] =>
    fields.flatMap((field) =>
        (facets?.find((entry) => entry.field === field.value)?.facets ?? [])
            .filter((bucket) => bucket.key.trim().length > 0)
            .map((bucket) => ({ field: field.value, label: field.label, value: bucket.key })),
    );

export const buildStageSuggestions = ({
    profile,
    assets,
    levels,
}: {
    profile: AssetProfile;
    assets: AssetRecord[];
    levels: LevelOption[];
}): { group: string; items: string[] }[] => {
    const names = [...new Set(assets.map((asset) => asset.label))].filter(Boolean);
    const ids = [...new Set(assets.map((asset) => asset.id))].filter((id) => !names.includes(id));
    const seen = new Set([...names, ...ids]);

    const machineGroups = [
        ...(names.length > 0 ? [{ group: "Name", items: names }] : []),
        ...(ids.length > 0 ? [{ group: profile.keyLabel, items: ids }] : []),
    ];

    const levelLabels = [...new Set(levels.map((level) => level.label))];
    const levelGroups = levelLabels.flatMap((label) => {
        const items = [
            ...new Set(
                levels
                    .filter((level) => level.label === label && !seen.has(level.value))
                    .map((level) => level.value),
            ),
        ];
        items.forEach((item) => {
            seen.add(item);
        });
        return items.length > 0 ? [{ group: label, items }] : [];
    });

    return [...machineGroups, ...levelGroups];
};

export const resolveStageSearch = ({
    assets,
    levels,
    text,
}: {
    assets: AssetRecord[];
    levels: LevelOption[];
    text: string;
}): StageSearchHit | undefined => {
    const query = text.trim().toLowerCase();
    if (!query) return undefined;

    const machine = assets.find(
        (asset) => asset.id.toLowerCase() === query || asset.label.toLowerCase() === query,
    );
    if (machine) return { machine };

    const level = levels.find((entry) => entry.value.toLowerCase() === query);
    if (level) return { level };

    const machinePartials = assets.filter(
        (asset) =>
            asset.label.toLowerCase().includes(query) || asset.id.toLowerCase().includes(query),
    );
    const levelPartials = levels.filter((entry) => entry.value.toLowerCase().includes(query));

    if (machinePartials.length + levelPartials.length !== 1) return undefined;
    return machinePartials.length === 1
        ? { machine: machinePartials[0] }
        : { level: levelPartials[0] };
};
